// ============================================================
// HTTP Client for Backend API
// Supports real API calls with mock fallback
// ============================================================

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8088";
// Mock fallback disabled - dashboard now uses real API only
const ENABLE_MOCK_FALLBACK = false; // process.env.NEXT_PUBLIC_ENABLE_MOCK_FALLBACK === 'true';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export class ApiError extends Error {
  status: number;
  /**
   * `error_code` dari respons backend, bila ada. Dipakai UI untuk membedakan
   * penolakan yang punya arti khusus (mis. `WABA_MISMATCH`) dari kegagalan
   * umum — pesan `error` bebas berubah, kode ini tidak.
   */
  errorCode?: string;
  constructor(message: string, status: number, errorCode?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
  }
}

// Token management
let authToken: string | null = null;

/** In-flight request controllers; aborted when session is cleared so stale 401s don't run. */
const activeRequests = new Set<AbortController>();

/**
 * Endpoint yang 401-nya BUKAN berarti "sesi Anda berakhir", jadi tidak boleh
 * memicu logout global.
 *
 * - `login` / `register` — belum ada sesi untuk diakhiri; 401 = kredensial salah
 * - `exchange` (SUBSC-20) — 401 = token handoff tidak berlaku. Halaman /masuk
 *   sudah menanganinya dengan layar kedaluwarsa; mendorong ke /login menghapus
 *   penjelasan itu sebelum sempat terbaca, dan pelanggan hanya melihat layar
 *   login seolah tidak terjadi apa-apa
 *
 * Sengaja sempit. Kalau melebar, sesi yang benar-benar kedaluwarsa berhenti
 * mengalihkan dan pengguna terjebak di halaman yang tidak bisa memuat apa pun.
 */
export function shouldTriggerGlobalLogout(path: string): boolean {
  return (
    !path.includes("/auth/login") &&
    !path.includes("/auth/register") &&
    !path.includes("/auth/exchange")
  );
}

/** Called on 401 (except auth/login, auth/register, auth/exchange). Use to clear session and redirect to login. */
let onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(callback: (() => void) | null) {
  onUnauthorized = callback;
}

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    if (typeof window !== "undefined") {
      localStorage.setItem("kustomer-token", token);
    }
  } else {
    if (typeof window !== "undefined") {
      localStorage.removeItem("kustomer-token");
    }
    // Abort in-flight requests so their 401 handlers don't run after re-login
    for (const ctrl of activeRequests) {
      ctrl.abort("logout");
    }
    activeRequests.clear();
  }
}

export function getAuthToken(): string | null {
  if (authToken) return authToken;
  if (typeof window !== "undefined") {
    authToken = localStorage.getItem("kustomer-token");
  }
  return authToken;
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

// Generic HTTP request
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: { timeout?: number }
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${path}`;
  const timeout = options?.timeout ?? 10000;
  const controller = new AbortController();
  activeRequests.add(controller);
  const timer = setTimeout(() => controller.abort(), timeout);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const tokenUsed = getAuthToken();
  if (tokenUsed) {
    headers["Authorization"] = `Bearer ${tokenUsed}`;
  }

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    clearTimeout(timer);
    activeRequests.delete(controller);

    let json: ApiResponse<T> & { error?: string };
    const text = await response.text();
    try {
      json = (text ? JSON.parse(text) : {}) as ApiResponse<T> & { error?: string };
    } catch {
      json = {} as ApiResponse<T> & { error?: string };
    }

    if (!response.ok) {
      const errorMsg =
        json && typeof json === "object" && typeof (json as { error?: string }).error === "string"
          ? (json as { error: string }).error
          : `HTTP ${response.status}`;
      console.error(`[HTTP] ❌ Error Response:`, {
        method,
        path,
        status: response.status,
        error: errorMsg,
        fullResponse: json,
      });

      // Only run global logout for 401 if this response is for the current session.
      // Stale 401s from requests sent with a previous token would otherwise destroy a new session.
      //
      // `/auth/exchange` dikecualikan (SUBSC-20): 401 di sana berarti "token
      // handoff ini tidak berlaku", BUKAN "sesi Anda berakhir". Halaman
      // /masuk sudah menanganinya dengan menampilkan layar kedaluwarsa —
      // mendorong ke /login justru menghapus penjelasan itu sebelum sempat
      // terbaca, dan pelanggan hanya melihat layar login seolah tidak
      // terjadi apa-apa.
      if (
        response.status === 401 &&
        shouldTriggerGlobalLogout(path) &&
        getAuthToken() === tokenUsed
      ) {
        onUnauthorized?.();
      }

      const errorCode = (json as { error_code?: string })?.error_code;
      throw new ApiError(errorMsg, response.status, typeof errorCode === "string" ? errorCode : undefined);
    }

    return json as ApiResponse<T>;
  } catch (error) {
    clearTimeout(timer);
    activeRequests.delete(controller);

    if (error instanceof ApiError) {
      // Already logged above
      throw error;
    }

    // Aborted (timeout or logout)
    if (error instanceof DOMException && error.name === "AbortError") {
      if (controller.signal.reason === "logout") {
        throw new ApiError("Session ended", 401);
      }
      console.error(`[HTTP] ⏱️ Request timeout for ${method} ${path} (${timeout}ms)`);
      throw new ApiError("Request timeout", 408);
    }

    // Generic network error
    console.error(`[HTTP] 🔴 Network error for ${method} ${path}:`, error);
    throw new ApiError(error instanceof Error ? error.message : "Network error", 0);
  }
}

// Public API methods
export const httpClient = {
  get: <T>(path: string, options?: { timeout?: number }) => request<T>("GET", path, undefined, options),

  post: <T>(path: string, body?: unknown, options?: { timeout?: number }) => request<T>("POST", path, body, options),

  put: <T>(path: string, body?: unknown, options?: { timeout?: number }) => request<T>("PUT", path, body, options),

  patch: <T>(path: string, body?: unknown, options?: { timeout?: number }) => request<T>("PATCH", path, body, options),

  delete: <T>(path: string, body?: unknown, options?: { timeout?: number }) =>
    request<T>("DELETE", path, body, options),
};

// Check if API is reachable
export async function isApiReachable(): Promise<boolean> {
  const healthUrl = `${API_BASE_URL}/health`;

  try {
    const response = await fetch(healthUrl, {
      method: "GET",
      signal: AbortSignal.timeout(3000),
    });

    if (response.ok) {
      try {
        const data = await response.json();
      } catch {
        // no JSON body
      }
    }

    return response.ok;
  } catch (error) {
    return false;
  }
}

// UUID validation — mock store IDs like "store-1" are not valid UUIDs
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isValidUUID(id: string): boolean {
  return UUID_REGEX.test(id);
}

export { ENABLE_MOCK_FALLBACK, API_BASE_URL };
